import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { getSupabase } from '../../lib/supabase-lazy';
import { colors } from '../../lib/colors';

interface TaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (task: Partial<ProjectTask>) => void;
  task: ProjectTask | null;
  projectId: string;
  existingTasks: ProjectTask[];
}

import { ProjectTask } from '../../types/project';

interface PlatformUser {
  id: string;
  full_name: string;
}

export default function TaskModal({ isOpen, onClose, onSave, task, projectId, existingTasks }: TaskModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    task_type: 'task' as 'milestone' | 'task' | 'compliance_task',
    status: 'pending' as 'pending' | 'in_progress' | 'completed' | 'blocked',
    assigned_to: '',
    due_date: '',
    depends_on: [] as string[],
  });
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [supabase, setSupabase] = useState<any>(null);

  useEffect(() => {
    getSupabase().then(setSupabase);
  }, []);

  useEffect(() => {
    if (task) {
      setFormData({
        name: task.name || task.title || '',
        description: task.description || '',
        task_type: task.task_type,
        status: task.status,
        assigned_to: task.assigned_to || '',
        due_date: task.due_date ? task.due_date.split('T')[0] : '',
        depends_on: task.depends_on || [],
      });
    }
  }, [task]);

  useEffect(() => {
    if (supabase) {
      fetchUsers();
    }
  }, [supabase]);

  const fetchUsers = async () => {
    if (!supabase) return;
    try {
      const { data, error } = await supabase
        .from('platform_users')
        .select('id, full_name')
        .order('full_name');

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error fetching users:', error);
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const taskData: Partial<ProjectTask> = {
      name: formData.name,
      description: formData.description || null,
      task_type: formData.task_type,
      status: formData.status,
      assigned_to: formData.assigned_to || null,
      due_date: formData.due_date || null,
      depends_on: formData.depends_on.length > 0 ? formData.depends_on : null,
    };

    onSave(taskData);
  };

  const availableDependencies = existingTasks.filter(t =>
    t.id !== task?.id &&
    (t.position_y || t.order_index || 0) < ((task?.position_y || task?.order_index) ?? Infinity)
  );

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />

      <div className="fixed inset-0 flex items-end sm:items-center justify-center p-0 sm:p-4">
        <Dialog.Panel className="relative transform overflow-hidden bg-white rounded-t-xl sm:rounded-xl shadow-xl transition-all w-full sm:max-w-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col">
          <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200">
            <Dialog.Title className="text-lg font-semibold text-gray-900">
              {task ? 'Edit Task' : 'Create New Task'}
            </Dialog.Title>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors rounded-lg p-2 sm:p-1 hover:bg-gray-100"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
            <div className="p-4 sm:p-6 space-y-4">
              {/* Task Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Task Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm placeholder-gray-400"
                  placeholder="Enter task name"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm placeholder-gray-400"
                  placeholder="Describe the task objectives and requirements"
                />
              </div>

              {/* Task Type and Status */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Type
                  </label>
                  <select
                    value={formData.task_type}
                    onChange={(e) => setFormData({ ...formData, task_type: e.target.value as any })}
                    className="mt-1 block w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="task">Task</option>
                    <option value="milestone">Milestone</option>
                    <option value="compliance_task">Compliance Task</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                    className="mt-1 block w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="blocked">Blocked</option>
                  </select>
                </div>
              </div>

              {/* Assigned To and Due Date */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Assigned To
                  </label>
                  <select
                    value={formData.assigned_to}
                    onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">Unassigned</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.full_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Due Date
                  </label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>

              {/* Dependencies */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Dependencies
                </label>
                <div className="mt-2 space-y-2 max-h-32 overflow-y-auto bg-gray-50 rounded-lg p-3 border border-gray-200">
                  {availableDependencies.map(depTask => (
                    <label key={depTask.id} className="flex items-center hover:bg-gray-100 p-1 rounded transition-colors cursor-pointer">
                      <input
                        type="checkbox"
                        value={depTask.id}
                        checked={formData.depends_on.includes(depTask.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ ...formData, depends_on: [...formData.depends_on, depTask.id] });
                          } else {
                            setFormData({ ...formData, depends_on: formData.depends_on.filter(id => id !== depTask.id) });
                          }
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-2 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">{depTask.name}</span>
                    </label>
                  ))}
                  {availableDependencies.length === 0 && (
                    <p className="text-sm text-gray-500">No available dependencies</p>
                  )}
                </div>
              </div>
            </div>

            {/* Actions - Fixed at bottom */}
            <div className="border-t border-gray-200 bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white rounded-lg shadow-sm transition-all duration-200 hover:shadow-md bg-blue-600 hover:bg-blue-700"
                style={window.innerWidth >= 768 ? {
                  background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)`,
                  boxShadow: `0 4px 12px -2px ${colors.brand.primary}40`
                } : undefined}
              >
                {task ? 'Update Task' : 'Create Task'}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}